import React, { FC } from 'react';
import { UseFormReturn, Controller } from 'react-hook-form';
import { Box } from '@mui/material';
import { FormDataType } from 'contexts/FormProvider';
import { useContentContext } from '@qbe/dfc-content';
import { FormGroupLabel, Button, CheckboxGroup } from '@qbe/ads-core';
import { FormCard } from '@qbe/ads-blocks-forms';
import { FormSpacer, InformationPopUp } from 'components';
import { noop } from 'lodash/fp';
import { SEPARATOR } from 'utils/constants';
import { camelCase } from 'lodash';
import { TrackEvent, formatNext } from 'hooks/useAnalytics';
import { useContentComplaintPartners } from 'utils/content';

interface Props {
  form: UseFormReturn<FormDataType>;
  show: boolean;
  isValid: boolean;
  showNextButton: boolean;
  onSubmit: () => void;
}

const RootCause: FC<Props> = ({ form, show, isValid = false, showNextButton = true, onSubmit = noop }) => {
  const { control, getValues } = form;

  const handleClick = () => {
    TrackEvent(formatNext('nextOtherDetails'), getValues());
    onSubmit();
  };

  const { getTextContent } = useContentComplaintPartners({ context: useContentContext() });

  const options = [
    ['People', 'Poor communication', 'Human error', 'Training and competence', 'Key person dependency'],
    [
      'Process',
      'Lack of adequate policies',
      'Lack of process',
      'Poor process design',
      'Inadequate monitoring',
      'Inadequate reporting',
      'Inadequate control',
      'Process change',
      'Failure to adhere to process',
      'Failure of governance process',
    ],
    ['System (IT)', 'Inadequate software', 'Inadequate infrastructure', 'Inadequate maintenance', 'Poor IT security'],
    [
      'External',
      'Customer expectations',
      'Infrastructure failure',
      'Third party failure',
      'Fraudulent action',
      'Malicious action',
      'Acts of nature',
    ],
  ];

  if (!show) return null;

  return (
    <FormCard variant="sectionTitle" title={getTextContent('breach.tell.us.more.rootCause')}>
      <FormGroupLabel
        alignCenter
        required
        legendElementOveride
        title={getTextContent('breach.tell.us.more.rootCause.heading')}
        titleSuffix={
          <InformationPopUp message={getTextContent('breach.tell.us.more.selectAll')} dataTrack="infoIconSelectAll" />
        }
      />
      <FormSpacer type="input" />
      {options.map(([heading, ..._options], index) => (
        <>
          {index > 0 && <FormSpacer type="formgroup" />}
          <Box
            sx={{
              maxWidth: '400px',
              width: '100%',
              '.MuiFormGroup-root': {
                width: '100%',
              },
            }}
          >
            <Controller
              control={control}
              name="rootCause"
              render={({ field }) => {
                return (
                  <CheckboxGroup
                    labelProps={{
                      alignCenter: true,
                    }}
                    onChange={(_, { checked, value: clickedValue }) => {
                      if (checked) {
                        field.onChange([...(field.value ?? []), clickedValue]);
                      } else {
                        field.onChange(field.value?.filter((value) => value !== clickedValue));
                      }
                    }}
                    key={heading}
                    title={heading}
                    checkboxList={_options.map((label) => ({
                      id: `${field.name}${SEPARATOR}${heading}${SEPARATOR}${camelCase(label)}`,
                      name: label,
                      value: `${heading}${SEPARATOR}${label}`,
                      label,
                      ['data-track']: 'rootCause',
                      ['data-track-value']: 'capture',
                    }))}
                  />
                );
              }}
            />
          </Box>
        </>
      ))}
      {showNextButton && (
        <>
          <FormSpacer type="formgroup" />
          <Button type="button" label={getTextContent('next.label')} disabled={!isValid} onClick={handleClick} />
        </>
      )}
    </FormCard>
  );
};

export default RootCause;
